/**
 * PlanCards — tarjetas de planes compartidas entre el registro (elegir plan)
 * y el upgrade in-app (free → premium). Los precios vienen de GET /api/plans
 * (misma fuente que el checkout); si falla, muestra los defaults.
 */
import { useEffect, useState } from 'react'

const API = import.meta.env.VITE_API_URL || '/api'

const fmtARS = (n) => '$' + Number(n).toLocaleString('es-AR')

const PLAN_DEFS = [
  {
    key: 'free', name: 'Free', tag: null, color: '#8e8e93',
    priceLabel: () => 'Gratis', per: 'para siempre',
    features: ['Monitor en vivo con tu Polar H10', 'Análisis completo del día', 'Historial de los últimos 7 días', 'Importación de sesiones'],
    cta: 'Empezar gratis',
  },
  {
    key: 'pro', name: 'Biohacker', tag: 'Completo', color: '#dc2626',
    priceLabel: (p) => fmtARS(p?.pro ?? 19990), per: 'pago único',
    features: ['Todo lo de Free', 'Historial completo (sin límite)', 'Control de fatiga & cuándo entrenar', 'Biofeedback en meditación', 'API Streaming OBS (Twitch / YouTube)', 'Prioridad en nuevas funciones'],
    cta: 'Elegir Biohacker',
  },
]

export default function PlanCards({ mode = 'register', currentPlan, onSelect, loadingPlan }) {
  const [prices, setPrices] = useState(null)
  const [paymentsEnabled, setPaymentsEnabled] = useState(true)   // optimista hasta saber
  useEffect(() => {
    fetch(`${API}/plans`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return
      setPrices(d.prices)
      setPaymentsEnabled(d.paymentsEnabled !== false)
    }).catch(() => {})
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {PLAN_DEFS.map(p => {
        const isCurrent = mode === 'upgrade' && p.key === (currentPlan ?? 'free')
        const busy      = loadingPlan === p.key
        const anyBusy   = !!loadingPlan
        const soon      = p.key !== 'free' && !paymentsEnabled   // pago aún no configurado
        const disabled  = isCurrent || anyBusy || soon
        return (
          <div key={p.key} style={{
            background: '#fff', borderRadius: 16, padding: '16px 18px', textAlign: 'left',
            border: p.tag ? `2px solid ${p.color}` : '1px solid #d9d9de',
            opacity: isCurrent ? 0.6 : 1, position: 'relative',
          }}>
            {p.tag && !isCurrent && (
              <span style={{
                position: 'absolute', top: -10, right: 14, background: p.color, color: '#fff',
                fontSize: 10, fontWeight: 800, borderRadius: 20, padding: '2px 10px', letterSpacing: 0.5,
              }}>{p.tag.toUpperCase()}</span>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <p style={{ fontSize: 16, fontWeight: 800, color: '#1c1c1e', margin: 0 }}>{p.name}</p>
              <p style={{ margin: 0 }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: p.color }}>{p.priceLabel(prices)}</span>
                <span style={{ fontSize: 11, color: '#8e8e93', marginLeft: 5 }}>{p.per}</span>
              </p>
            </div>
            <ul style={{ margin: '10px 0 12px', padding: 0, listStyle: 'none' }}>
              {p.features.map(f => (
                <li key={f} style={{ fontSize: 12.5, color: '#3a3a3c', padding: '2.5px 0', display: 'flex', gap: 7 }}>
                  <span style={{ color: p.key === 'free' ? '#8e8e93' : p.color, fontWeight: 700 }}>✓</span>{f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => !disabled && onSelect(p.key)}
              disabled={disabled}
              style={{
                width: '100%', border: 'none', borderRadius: 11, padding: '12px', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer',
                background: isCurrent || soon ? '#f2f2f7' : p.key === 'free' ? '#1c1c1e' : p.color,
                color: isCurrent || soon ? '#8e8e93' : '#fff',
                opacity: anyBusy && !busy ? 0.5 : 1,
              }}>
              {isCurrent ? 'Tu plan actual'
                : soon ? 'Disponible muy pronto'
                : busy ? 'Abriendo pago…'
                : p.cta}
            </button>
          </div>
        )
      })}
      <p style={{ fontSize: 11, color: mode === 'register' ? 'rgba(255,255,255,0.35)' : '#8e8e93', textAlign: 'center', margin: '2px 0 0', lineHeight: 1.5 }}>
        Los planes pagos se abonan por MercadoPago. El plan Free guarda tus últimos 7 días de datos.
      </p>
    </div>
  )
}
